import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import ContactForm from '../components/ContactForm'
import SEO from '../components/SEO'
import { HOME_META } from '../data/meta'

const DEFAULT_HOME_PLANS = [
  { name: 'Starter', price: '15,000', period: 'one time', maintenance: '3,000', features: ['TRED-EX AI', 'Basic setup & training', 'Email support', '30-day optimization'], badge: '', featured: false },
  { name: 'Growth', price: '35,000', period: 'one time', maintenance: '7,000', features: ['3-5 AI features', 'Advanced setup & integrations', 'Priority support', 'Monthly optimization', 'Performance analytics'], badge: 'Most Popular', featured: true },
  { name: 'Enterprise', price: '75,000', period: 'one time', maintenance: '15,000', features: ['Full AI stack', 'Custom integrations', 'Dedicated account manager', 'SLA guarantee', 'Quarterly strategy reviews'], badge: '', featured: false },
]

const encodeEmail = (local, domain) => {
  const email = `${local}@${domain}`
  const labels = email.split('').map((c) => `&#${c.charCodeAt(0)};`).join('')
  return { mailto: email, label: labels }
}

export default function Home() {
  const counterRef = useRef(null)
  const statsRef = useRef(null)
  const carouselRef = useRef(null)
  const location = useLocation()
  const supportEmail = encodeEmail('support', 'tredextechnology.in')
  const [pricing, setPricing] = useState({ symbol: '₹', plans: DEFAULT_HOME_PLANS })

  const SmilingRobot = () => (
    <svg viewBox="0 0 64 64" width="28" height="28" aria-hidden="true" style={{ display: 'block' }}>
      <line x1="32" y1="9" x2="32" y2="15" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <circle cx="32" cy="6" r="3" fill="currentColor" />
      <rect x="12" y="15" width="40" height="34" rx="11" fill="none" stroke="currentColor" strokeWidth="4" />
      <rect x="7" y="26" width="6" height="12" rx="3" fill="currentColor" />
      <rect x="51" y="26" width="6" height="12" rx="3" fill="currentColor" />
      <circle cx="24" cy="29" r="3" fill="currentColor" />
      <circle cx="40" cy="29" r="3" fill="currentColor" />
      <path d="M22 37 Q32 46 42 37" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  )

  useEffect(() => {
    let cancelled = false
    fetch('/api/pricing')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data || !data.plans) return
        setPricing({ symbol: data.symbol || '₹', plans: data.plans })
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const animateCounters = (root) => {
    if (!root) return

    const counters = root.querySelectorAll('.stat-number')

    counters.forEach(counter => {
      const targetValue = counter.getAttribute('data-target')

      // Skip static values like 24/7
      if (!targetValue) return

      const target = parseInt(targetValue)
      const increment = target / 200
      let current = 0

      const updateCount = () => {
        current += increment

        if (current < target) {
          counter.textContent = Math.ceil(current)
          requestAnimationFrame(updateCount)
        } else {
          counter.textContent = target
        }
      }

      updateCount()
    })
  }


    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          animateCounters(entry.target)
          observer.unobserve(entry.target)
        }
      })
    }, { threshold: 0.5 })

    if (counterRef.current) observer.observe(counterRef.current)
    if (statsRef.current) observer.observe(statsRef.current)

    const handleScroll = () => {
      const scrolled = window.scrollY
      const shapes = document.querySelectorAll('.hero-shape')
      shapes.forEach((shape, index) => {
        const speed = 0.1 * (index + 1)
        shape.style.transform = `translateY(${scrolled * speed}px)`
      })
    }
    window.addEventListener('scroll', handleScroll)

    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1)
      const element = document.getElementById(id)
      if (element) {
        const navHeight = 80
        const elementPosition = element.getBoundingClientRect().top + window.scrollY - navHeight
        setTimeout(() => {
          window.scrollTo({
            top: elementPosition,
            behavior: 'smooth'
          })
        }, 100)
      }
    }
  }, [location])

  const scrollCarousel = (direction) => {
    if (!carouselRef.current) return
    const card = carouselRef.current.querySelector('.conversion-card')
    if (!card) return
    const style = getComputedStyle(carouselRef.current)
    const gap = parseFloat(style.gap) || 16
    const scrollAmount = card.offsetWidth + gap
    const maxScroll = carouselRef.current.scrollWidth - carouselRef.current.clientWidth
    const target = Math.min(Math.max(carouselRef.current.scrollLeft + direction * scrollAmount, 0), maxScroll)
    carouselRef.current.scrollTo({ left: target, behavior: 'smooth' })
  }

  return (
    <>
      <SEO
        title={HOME_META.title}
        description={HOME_META.description}
        canonical={HOME_META.canonical}
        keywords={HOME_META.keywords}
        structuredData={HOME_META.structuredData}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-bg">
          <div className="hero-shape hero-shape-1"></div>
          <div className="hero-shape hero-shape-2"></div>
          <div className="hero-shape hero-shape-3"></div>
        </div>
        <div className="container hero-content">
          <div className="hero-text">
            <span className="hero-badge">✦ &nbsp;Built to Win Customers &nbsp; ✦</span>
           <h1
  className="hero-title"
  style={{
    color: "#102A5C",
    fontWeight: "900",
    letterSpacing: "-1.5px",
    lineHeight: "1.05"
  }}
>
  AI Sales Assistant <br />

  <span
    className="hero-highlight"
    style={{
      background:
        "linear-gradient(90deg, #0066FF 0%, #6C3BFF 45%, #FF8A00 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      fontWeight: "900",
      filter: "drop-shadow(0 3px 8px rgba(0, 102, 255, 0.15))"
    }}
  >
    that Finds, Engages & Converts!
  </span>
</h1>
                        <div className="hero-auto-pill">
              <span className="hero-auto-pill-icon"><i className="fas fa-bolt"></i></span>
              <span className="hero-auto-pill-text">Every Visitor is a Potential Customer</span> <span className="hero-auto-pill-icon"><i className="fas fa-bolt"></i></span>
            </div>
            <p className="hero-description">
              More than just a Chatbot. <br /><br />
              It Auto-Identifies High potential visitors, Starts Smart Conversations, Recommends the right products, Answers Instantly and Helps Convert more visitors into loyal customers. 
              
              
            </p>
         
            
            
            <div className="hero-buttons">
              <Link to="/contact" className="btn btn-primary btn-lg">
                                Get Started <i className="fas fa-arrow-right"></i>
              </Link>
              <Link to="/services" className="btn btn-outline btn-lg">
                View Services <i className="fas fa-th-large"></i>
              </Link>
            </div>
            <div className="hero-features">
              <div className="hero-feature-item">
                <div className="hero-feature-icon"><i className="fas fa-user-check"></i></div>
                <div>
                  <strong>Identify Potential Customers</strong>
                  <span>AI analyzes visitor behavior and recognizes high-intent customers.</span>
                </div>
              </div>
              <div className="hero-feature-item">
                <div className="hero-feature-icon"><i className="fas fa-comments"></i></div>
                <div>
                  <strong>Smart Engagement</strong>
                  <span>Starts real-time, personalized conversations automatically.</span>
                </div>
              </div>
              <div className="hero-feature-item">
                <div className="hero-feature-icon"><i className="fas fa-shopping-bag"></i></div>
                <div>
                  <strong>Auto Recommend</strong>
                  <span>Suggests the right products based on interest, behavior & trends.</span>
                </div>
              </div>
              <div className="hero-feature-item">
                <div className="hero-feature-icon"><i className="fas fa-chart-line"></i></div>
                <div>
                  <strong>Drive Conversions</strong>
                  <span>Helps customers decide faster and boosts sales and revenue.</span>
                </div>
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="hero-chat-mockup">
              <div className="chat-mockup-header">
                <div className="chat-mockup-avatar"><SmilingRobot /></div>
                <div className="chat-mockup-info">
                  <div className="chat-mockup-name">TRED-EX AI Assistant</div>
                  <div className="chat-mockup-status"><span className="status-dot"></span> Online</div>
                </div>
                <div className="chat-mockup-controls">
                  <i className="fas fa-trash"></i>
                  
                  <i className="fas fa-times"></i>
                </div>
              </div>
              <div className="chat-mockup-body">
                <div className="chat-bubble ai">
                  <div className="chat-bubble-text">Hi! 👋 I'm your AI Sales Assistant. I can help you find the best products, answer questions and give you exclusive offers!</div>
                </div>
                <div className="chat-time-row">10:30 AM</div>
                <div className="chat-bubble user">
                  <div className="chat-bubble-text">Looking for wireless earbuds under ₹2,000</div>
                </div>
                <div className="chat-time-row user">10:30 AM</div>
                <div className="chat-bubble ai">
                  <div className="chat-bubble-text">Great choice! Here are some top rated wireless earbuds for you 👇</div>
                </div>
              
 <>
  <style>{`
  
    .chat-product-grid {
      display: grid !important;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 10px;
      width: 100%;
      margin: 12px 0 8px;
    }

    .chat-product-grid .chat-product-card {
      position: relative;
      overflow: hidden;
      min-width: 0;
      padding: 12px 9px 13px;
      border-radius: 18px;

      background: linear-gradient(
        145deg,
        #ffffff 0%,
        #f8f7ff 100%
      );

      border: 1px solid rgba(99, 102, 241, 0.12);

      box-shadow:
        0 7px 20px rgba(31, 41, 55, 0.08),
        0 2px 5px rgba(31, 41, 55, 0.04);

      text-align: center;

      transition:
        transform 0.25s ease,
        box-shadow 0.25s ease,
        border-color 0.25s ease;
    }

    .chat-product-grid .chat-product-card:hover {
      transform: translateY(-4px);

      border-color: rgba(99, 102, 241, 0.28);

      box-shadow:
        0 14px 30px rgba(31, 41, 55, 0.13),
        0 4px 12px rgba(99, 102, 241, 0.08);
    }


    /* PRODUCT IMAGE */

    .chat-product-grid .chat-product-icon {
      width: 58px;
      height: 58px;

      margin: 0 auto 10px;

      display: flex;
      align-items: center;
      justify-content: center;

      border-radius: 17px;

      background: linear-gradient(
        135deg,
        #f3efff,
        #e9e3ff
      );

      border: 1px solid rgba(124, 58, 237, 0.10);

      box-shadow:
        0 6px 15px rgba(124, 58, 237, 0.10);

      overflow: hidden;

      transition:
        transform 0.25s ease,
        box-shadow 0.25s ease;
    }

    .chat-product-grid .chat-product-card:nth-child(2)
    .chat-product-icon {
      background: linear-gradient(
        135deg,
        #edf7ff,
        #dfefff
      );

      border-color: rgba(37, 99, 235, 0.10);

      box-shadow:
        0 6px 15px rgba(37, 99, 235, 0.10);
    }

    .chat-product-grid .chat-product-card:nth-child(3)
    .chat-product-icon {
      background: linear-gradient(
        135deg,
        #fff5ed,
        #ffeadc
      );

      border-color: rgba(249, 115, 22, 0.10);

      box-shadow:
        0 6px 15px rgba(249, 115, 22, 0.10);
    }

    .chat-product-grid .chat-product-card:hover
    .chat-product-icon {
      transform: scale(1.07);

      box-shadow:
        0 10px 20px rgba(99, 102, 241, 0.16);
    }


    /* ACTUAL EXTERNAL PRODUCT IMAGE */

   .chat-product-grid .chat-product-icon img {
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;

  display: block;

  object-fit: cover;
  object-position: center;

  border-radius: 17px;

  transform: scale(1.02);
}

    /* PRODUCT NAME */

    .chat-product-grid .chat-product-name {
      min-height: 32px;

      display: flex;
      align-items: center;
      justify-content: center;

      color: #111827;

      font-size: 12px;
      line-height: 1.35;
      font-weight: 800;

      margin-bottom: 5px;
    }


    /* PRICE */

    .chat-product-grid .chat-product-price {
      color: #6d28d9;

      font-size: 15px;
      line-height: 1;

      font-weight: 900;

      margin-bottom: 8px;
    }

    .chat-product-grid .chat-product-card:nth-child(2)
    .chat-product-price {
      color: #2563eb;
    }

    .chat-product-grid .chat-product-card:nth-child(3)
    .chat-product-price {
      color: #ea580c;
    }


    /* RATING */

    .chat-product-grid .chat-product-rating {
      display: inline-flex;
      align-items: center;
      justify-content: center;

      gap: 2px;

      padding: 4px 7px;

      border-radius: 7px;

      background: #fff7d6;

      color: #f59e0b;

      font-size: 9px;

      box-shadow:
        0 2px 6px rgba(245, 158, 11, 0.10);
    }

    .chat-product-grid .chat-product-rating i {
      font-size: 8px;
    }


    /* MOBILE */

    @media (max-width: 600px) {

      .chat-product-grid {
        gap: 8px;
      }

      .chat-product-grid .chat-product-card {
        padding: 11px 7px 12px;
        border-radius: 15px;
      }

      .chat-product-grid .chat-product-icon {
        width: 48px;
        height: 48px;
        border-radius: 14px;
        margin-bottom: 8px;
      }

      .chat-product-grid .chat-product-icon img {
        padding: 5px;
        border-radius: 14px;
      }

      .chat-product-grid .chat-product-name {
        font-size: 11px;
      }

      .chat-product-grid .chat-product-price {
        font-size: 14px;
      }

      .chat-product-grid .chat-product-rating {
        font-size: 8px;
        padding: 4px 6px;
      }

      .chat-product-grid .chat-product-rating i {
        font-size: 7px;
      }
    }
  `}</style>


  <div className="chat-product-grid">

    {/* PRODUCT 1 */}

    <div className="chat-product-card">

      <div className="chat-product-icon">
        <img
          src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQdBOEizdoGrC4lrTn8Ms3Xd6v_HayqrLuX-_6CraU7mQ&s=10"
          alt="BassPods X1"
        />
      </div>

      <div className="chat-product-name">
        BassPods X1
      </div>

      <div className="chat-product-price">
        ₹1,499
      </div>

      <div className="chat-product-rating">
        <i className="fas fa-star"></i>
        <i className="fas fa-star"></i>
        <i className="fas fa-star"></i>
        <i className="fas fa-star"></i>
        <i className="fas fa-star"></i>
      </div>

    </div>


    {/* PRODUCT 2 */}

    <div className="chat-product-card">

      <div className="chat-product-icon">
        <img
          src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS8bBml2eOi50F6R1IJnZFIpan-nMDYs_5rv-rS4AWcrg&s=10"
          alt="SoundBeat Pro"
        />
      </div>

      <div className="chat-product-name">
        SoundBeat Pro
      </div>

      <div className="chat-product-price">
        ₹1,899
      </div>

      <div className="chat-product-rating">
        <i className="fas fa-star"></i>
        <i className="fas fa-star"></i>
        <i className="fas fa-star"></i>
        <i className="fas fa-star"></i>
        <i className="fas fa-star"></i>
      </div>

    </div>


    {/* PRODUCT 3 */}

    <div className="chat-product-card">

      <div className="chat-product-icon">
        <img
          src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTSKK6HAoYxCDPNPKdSqllnVNIj0HTOaA5LZTy5U5AmR45wfZc0rXNaOX0&s=10"
          alt="TuneFlex Lite"
        />
      </div>

      <div className="chat-product-name">
        TuneFlex Lite
      </div>

      <div className="chat-product-price">
        ₹1,799
      </div>

      <div className="chat-product-rating">
        <i className="fas fa-star"></i>
        <i className="fas fa-star"></i>
        <i className="fas fa-star"></i>
        <i className="fas fa-star"></i>
        <i className="fas fa-star-half-alt"></i>
      </div>

    </div>

  </div>
</>

                <div className="chat-time-row">10:31 AM</div>
                <div className="chat-bubble user">
                  <div className="chat-bubble-text">Which one has the best battery?</div>
                </div>
                <div className="chat-time-row user">10:31 AM</div>
                <div className="chat-bubble ai">
                  <div className="chat-bubble-text">TuneFlex Lite gives up to 28 hrs battery life 🔋 and fast charging. Would you like to add it to cart?</div>
                </div>
                <div className="chat-time-row">10:32 AM</div>
              </div>
              <div className="chat-mockup-footer">
                <input type="text" placeholder="Type your message..." readOnly />
                <div className="chat-footer-actions">
                  <button className="chat-action-btn"><i className="fas fa-microphone"></i></button>
                  <button className="chat-send-btn"><i className="fas fa-paper-plane"></i></button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conversion Chat Showcase */}
      <section className="conversion-showcase">
        <div className="container">
          <div className="conversion-header">
            <span className="section-badge">AI That Doesn't Just Chat. It Tries to Convert.</span>
            <h2 className="section-title">Turn More Visitors into <span className="gradient-text">Customers</span></h2>
            <p className="section-description">Every visitor is a potential customer. TRED-EX AI doesn't simply answer questions. It engages, understands intent, handles hesitation, recommends the right choice, and keeps the conversation moving toward conversion.</p>
          </div>
          <div className="conversion-carousel-wrapper">
            <div className="conversion-carousel" ref={carouselRef}>
              {[
                {
                  scenario: "When They're 'Just Browsing'",
                  desc: "Turn passive visitors into active conversations and converts them.",
                  bubbles: [
                    { who: 'user', text: "I'm just browsing for now." },
                    { who: 'ai', text: "Of course! 😊 Take your time." },
                    { who: 'ai', text: "Since you're looking at wireless earbuds, I can show you our top picks under ₹2,000 so you don't have to search through everything." },
                    { who: 'user', text: "Okay, show me." },
                    { who: 'ai', text: "Absolutely! 🎯 Based on what you're looking for, here are the best matches." },
                  ],
                  products: true,
                  cta: null,
                },
                {
                  scenario: "When They're Comparing Options",
                  desc: "Helps Customers choose instead of letting them leave.",
                  bubbles: [
                    { who: 'user', text: "I'm not sure which one to buy." },
                    { who: 'ai', text: "No problem! 😊 Let me help you choose. What's most important to you?" },
                    { who: 'ai', text: "🔋 Battery\n🎵 Sound Quality\n💰 Price", isChoice: true },
                    { who: 'user', text: "Battery." },
                    { who: 'ai', text: "Then I'd go with TuneFlex Lite. 🎯 It gives you up to 28 hours of battery life and fast charging. Based on what you've told me, this is the one I'd pick for you. 🛒" },
                    { who: 'user', text: "Then I'd go with TuneFlex Lite. But I'm worried about warranty" },
                    { who: 'ai', text: "They have 6 month waranty which will come inside the box." },
                  ],
                  products: false,
                },
                {
                  scenario: "When They're Ready to Buy",
                  desc: "Remove the final friction and move them toward checkout.",
                  bubbles: [
                    { who: 'user', text: "Does it have Cash on Delivery?" },
                    { who: 'ai', text: "Yes! 😊 Cash on Delivery is available." },
                    { who: 'user', text: "Okay, I'll take it." },
                    { who: 'ai', text: "Excellent choice! 🎉" },
                    { who: 'ai', text: "Shall I help you complete the order? 🛍️", product: { name: 'TuneFlex Lite', price: '₹1,799' } },
                    { who: 'user', text: "yes" },
                    { who: 'ai', text: "Excellent choice! Your Order have been placed 🎉" },
                    { who: 'ai', text: "Enjoy your day 🎉" },
                  ],
                  products: false,
                  cta: { text: 'View Orders →', primary: true },
                },
              ].map((card, i) => (
                <div className="conversion-card" key={i}>
                  <div className="scenario-title">{card.scenario}</div>
                  <div className="scenario-desc">{card.desc}</div>
                  <div className="hero-chat-mockup">
                    <div className="chat-mockup-header">
                      <div className="chat-mockup-avatar"><SmilingRobot /></div>
                      <div className="chat-mockup-info">
                        <div className="chat-mockup-name">TRED-EX AI Assistant</div>
                        <div className="chat-mockup-status"><span className="status-dot"></span> Online</div>
                      </div>
                      <div className="chat-mockup-controls">
                        <i className="fas fa-trash"></i>
                        <i className="fas fa-times"></i>
                      </div>
                    </div>
                    <div className="chat-mockup-body">
                      {card.bubbles.map((b, j) => (
                        <div key={j}>
                          <div className={`chat-time-row ${b.who === 'user' ? 'user' : ''}`}>
                            {b.who === 'user' ? '10:30 AM' : '10:30 AM'}
                          </div>
                          <div className={`chat-bubble ${b.who}`}>
                            <div className="chat-bubble-text" style={{ whiteSpace: b.isChoice ? 'pre-line' : 'normal' }}>
                              {b.text}
                            </div>
                          </div>
                          {b.product && (
                            <div className="chat-product-highlight">
                              <div className="chat-product-highlight-name">{b.product.name}</div>
                              <div className="chat-product-highlight-price">{b.product.price}</div>
                            </div>
                          )}
                        </div>
                      ))}
                      {card.products && (
                        <>
                          <div className="chat-time-row">10:31 AM</div>
                          <div className="chat-product-grid">
                            {[
                              { name: 'BassPods X1', price: '₹1,499', rating: 5, img: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQdBOEizdoGrC4lrTn8Ms3Xd6v_HayqrLuX-_6CraU7mQ&s=10' },
                              { name: 'SoundBeat Pro', price: '₹1,899', rating: 5, img: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS8bBml2eOi50F6R1IJnZFIpan-nMDYs_5rv-rS4AWcrg&s=10' },
                              { name: 'TuneFlex Lite', price: '₹1,799', rating: 4.5, img: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTSKK6HAoYxCDPNPKdSqllnVNIj0HTOaA5LZTy5U5AmR45wfZc0rXNaOX0&s=10' },
                            ].map((product, pi) => (
                              <div className="chat-product-card" key={pi}>
                                <div className="chat-product-icon">
                                  <img src={product.img} alt={product.name} />
                                </div>
                                <div className="chat-product-name">{product.name}</div>
                                <div className="chat-product-price">{product.price}</div>
                                <div className="chat-product-rating">
                                  {[...Array(5)].map((_, si) => (
                                    <i key={si} className={`fas fa-star${si === 4 && product.rating === 4.5 ? '-half-alt' : ''}`}></i>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                      {card.cta && (
                        <div className="chat-cta-row">
                          <span className="chat-cta-btn">{card.cta.text}</span>
                        </div>
                      )}
                    </div>
                    <div className="chat-mockup-footer">
                      <input type="text" placeholder="Type your message..." readOnly />
                      <div className="chat-footer-actions">
                        <button className="chat-action-btn"><i className="fas fa-microphone"></i></button>
                        <button className="chat-send-btn"><i className="fas fa-paper-plane"></i></button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button className="carousel-btn carousel-prev" onClick={() => scrollCarousel(-1)} aria-label="Previous">
              <i className="fas fa-chevron-left"></i>
            </button>
            <button className="carousel-btn carousel-next" onClick={() => scrollCarousel(1)} aria-label="Next">
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
          <div className="conversion-cta">
            <p className="conversion-closing-text">Don't let interested visitors leave empty-handed. TRED-EX AI works around the clock to engage visitors, understand what they want, overcome hesitation, and guide more conversations toward conversion.</p>
            <Link to="/contact" className="btn btn-primary btn-lg">See How TRED-EX AI Can Sell For You <i className="fas fa-arrow-right"></i></Link>
          </div>
        </div>
      </section>

      {/* Featured Service - AI Chatbot */}
      <section className="featured-service">
        <div className="container">
          <div className="featured-service-grid">
            <div className="featured-service-content">
              <span className="featured-badge">⚡ YOUR WEBSITE'S AI SALES REP</span>
              <h2 className="section-title">Your Website Can <span className="gradient-text">Sell While You Sleep.</span></h2>
              <p className="featured-service-desc">
                TRED-EX AI turns your website into an always-on sales channel engaging visitors, understanding what they need, answering questions, capturing leads, and guiding them toward the next step.
              </p>
              <ul className="featured-capabilities">
                <li><span className="capability-icon">🎯</span> <strong>Detects Intent</strong> Recognizes visitors who are showing genuine buying interest.</li>
                <li><span className="capability-icon">💬</span> <strong>Starts the Conversation</strong> Engages visitors instead of waiting for them to ask the perfect question.</li>
                <li><span className="capability-icon">🧠</span> <strong>Guides the Decision</strong> Recommends the right product, service, or next step based on what the visitor needs.</li>
                <li><span className="capability-icon">🚀</span> <strong>Pushes Toward Conversion</strong>
                <br /> Handles questions and hesitation, captures leads, and guides visitors toward taking action.</li>
              </ul>
              <div className="featured-buttons">
                <Link to="/contact" className="btn btn-primary btn-lg">Put an AI Salesperson on Your Website <i className="fas fa-arrow-right"></i></Link>
                <Link to="/services" className="btn btn-outline btn-lg">See It In Action <i className="fas fa-play-circle"></i></Link>
              </div>
            </div>
            <div className="featured-service-visual">
              <div className="hero-chat-mockup">
                  <div className="chat-mockup-header">
                    <div className="chat-mockup-avatar"><SmilingRobot /></div>
                    <div className="chat-mockup-info">
                      <div className="chat-mockup-name">TRED-EX AI Assistant</div>
                      <div className="chat-mockup-status"><span className="status-dot"></span> Online</div>
                    </div>
                  </div>
                  <div className="chat-mockup-body">
                    <div className="chat-bubble ai">
                      <div className="chat-bubble-text">Hi! 👋 Looking for something specific today?</div>
                    </div>
                    <div className="chat-time-row user">10:30 AM</div>
                    <div className="chat-bubble user">
                      <div className="chat-bubble-text">I need wireless earbuds under ₹2,000.</div>
                    </div>
                    <div className="chat-time-row">10:30 AM</div>
                    <div className="chat-bubble ai">
                      <div className="chat-bubble-text">Absolutely! 🎯 What matters most to you — battery life, sound quality, or price?</div>
                    </div>
                    <div className="chat-time-row user">10:31 AM</div>
                    <div className="chat-bubble user">
                      <div className="chat-bubble-text">Battery life.</div>
                    </div>
                    <div className="chat-time-row">10:31 AM</div>
                    <div className="chat-bubble ai">
                      <div className="chat-bubble-text">Then I have a great match for you. 🔋 <strong>TuneFlex Lite — ₹1,799</strong><br />Up to 28 hours battery + fast charging.</div>
                    </div>
                    <div className="chat-time-row">10:32 AM</div>
                    <div className="chat-bubble ai">
                      <div className="chat-bubble-text">Want me to help you get it?</div>
                    </div>
                    <div className="chat-cta-row">
                      <span className="chat-cta-btn">Yes</span>
                    </div>
                  </div>
                  <div className="chat-mockup-footer">
                    <input type="text" placeholder="Type your message..." readOnly />
                    <div className="chat-footer-actions">
                      <button className="chat-action-btn"><i className="fas fa-microphone"></i></button>
                      <button className="chat-send-btn"><i className="fas fa-paper-plane"></i></button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
         <center> <div className="featured-status-strip">
            <span className="featured-status-dot"></span>
            <span className="featured-status-label">AI Sales Assistant — Working</span>
            <span className="featured-status-divider">|</span>
            <span>Visitors engaged: 12</span>
            <span className="featured-status-divider">|</span>
            <span>High-intent leads: 4</span>
            <span className="featured-status-divider">|</span>
            <span>Conversations handled: 27</span>
          </div></center>
      
      </section>

      {/* Ecommerce Industries Section */}
      <section className="ecommerce-industries">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Perfect for E-commerce & All Businesses</span>
            <h2 className="section-title">Turn More Visitors into Customers with <span className="gradient-text">AI-Powered Conversations!</span></h2>
            <p className="section-description">Our AI Sales Assistant works across every industry. From online stores to local services, we help you engage visitors, recommend products, and close more sales.</p>
          </div>
          <div className="industries-grid">
            {[
              { icon: 'fa-store', name: 'Online Stores', desc: 'Boost cart value and reduce abandonment' },
              { icon: 'fa-tshirt', name: 'Fashion & Apparel', desc: 'Personalized style recommendations' },
              { icon: 'fa-mobile-alt', name: 'Electronics & Gadgets', desc: 'Compare specs and suggest best fit' },
              { icon: 'fa-spa', name: 'Beauty & Wellness', desc: 'Product guidance and booking' },
              { icon: 'fa-couch', name: 'Home & Lifestyle', desc: 'Room-based suggestions and offers' },
              { icon: 'fa-utensils', name: 'Food & Beverages', desc: 'Order assistance and combo deals' },
              { icon: 'fa-th-large', name: '& Many More Industries', desc: 'Custom AI for your business type' },
            ].map((industry, i) => (
              <div className="industry-card" key={i}>
                <div className="industry-icon"><i className={`fas ${industry.icon}`}></i></div>
                <h4>{industry.name}</h4>
                <p>{industry.desc}</p>
              </div>
            ))}
          </div>
          <div className="ecommerce-cta">
            <Link to="/contact" className="btn btn-primary btn-lg">Grow Your Business. 24/7. <i className="fas fa-arrow-right"></i></Link>
          </div>
        </div>
      </section>

      {/* Problem / Solution Section */}
      <section className="section-problem">
        <div className="container">
          <div className="problem-grid">
            <div className="problem-card problem-bad">
              <div className="problem-icon"><i className="fas fa-times-circle"></i></div>
              <h3>The Problem</h3>
              <p>Your competitors are using AI to capture leads, automate support, and personalize experiences. Your static website is losing customers 24/7.</p>
              <ul className="problem-list">
                <li><i className="fas fa-times"></i> Visitors leave without converting</li>
                <li><i className="fas fa-times"></i> Support emails pile up after hours</li>
                <li><i className="fas fa-times"></i> Customers can't find what they need</li>
                <li><i className="fas fa-times"></i> No personalization or recommendations</li>
              </ul>
            </div>
            <div className="problem-divider">
              <div className="problem-divider-line"></div>
              <div className="problem-divider-icon"><i className="fas fa-arrow-right"></i></div>
              <div className="problem-divider-line"></div>
            </div>
            <div className="problem-card problem-good">
              <div className="problem-icon"><i className="fas fa-check-circle"></i></div>
              <h3>The TRED-EX AI Solution</h3>
              <p>We add smart AI features to your existing website, so no rebuild needed. Your site becomes an intelligent business engine that works while you sleep.</p>
              <ul className="problem-list">
                <li><i className="fas fa-check"></i> AI chatbot captures leads 24/7</li>
                <li><i className="fas fa-check"></i> Smart search & recommendations</li>
                <li><i className="fas fa-check"></i> Automated SEO & content</li>
                <li><i className="fas fa-check"></i> Lead qualification & routing</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section id="services" className="services-overview">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">AI Services</span>
            <h2 className="section-title">AI Features We <span className="gradient-text">Install For You</span></h2>
            <p className="section-description">
              Choose individual features or let us build a complete AI stack. Every feature is tailored to your business type.
            </p>
          </div>
          <div className="services-categories">
            <div className="service-category">
              <div className="service-category-header">
                <div className="service-category-icon"><i className="fas fa-comments"></i></div>
                <div>
                  <h3>Customer-Facing AI</h3>
                  <p>Features your visitors interact with directly</p>
                </div>
              </div>
              <div className="service-category-features">
                {[
                  { icon: 'fa-robot', title: 'AI Chatbot / Ask Us Widget', desc: 'Trained on your site content, FAQs, and policies' },
                  { icon: 'fa-lightbulb', title: 'AI Product/Service Recommender', desc: '"Not sure what you need? Tell us and we\'ll suggest"' },
                  { icon: 'fa-search', title: 'Smart Search', desc: 'Natural language search instead of keywords' },
                  { icon: 'fa-calculator', title: 'AI Quote/Estimate Generator', desc: 'User describes need, gets instant estimate' },
                  { icon: 'fa-microphone', title: 'Voice Assistant', desc: 'Accessibility and hands-free browsing' },
                  { icon: 'fa-language', title: 'AI Multilingual Translation', desc: 'Context-aware translation, not just a widget' },
                  { icon: 'fa-star', title: 'AI Review Summarizer', desc: '"What customers are saying" digest' },
                ].map((feature, i) => (
                  <div className="feature-list-item" key={i}>
                    <div className="feature-list-icon"><i className={`fas ${feature.icon}`}></i></div>
                    <div>
                      <strong>{feature.title}</strong>
                      <p>{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="service-category">
              <div className="service-category-header">
                <div className="service-category-icon"><i className="fas fa-chart-line"></i></div>
                <div>
                  <h3>Lead-Gen & Sales Boosters</h3>
                  <p>Turn more visitors into paying customers</p>
                </div>
              </div>
              <div className="service-category-features">
                {[
                  { icon: 'fa-filter', title: 'AI Lead Qualifier', desc: 'Chatbot that scores and routes leads' },
                  { icon: 'fa-calendar-check', title: 'Appointment Booking Assistant', desc: 'Integrated with your calendar' },
                  { icon: 'fa-shopping-cart', title: 'Exit-Intent AI Popup', desc: 'Personalized offers before they leave' },
                  { icon: 'fa-envelope-open-text', title: 'AI Follow-Up Emails', desc: 'Personalized emails triggered by behavior' },
                ].map((feature, i) => (
                  <div className="feature-list-item" key={i}>
                    <div className="feature-list-icon"><i className={`fas ${feature.icon}`}></i></div>
                    <div>
                      <strong>{feature.title}</strong>
                      <p>{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="service-category">
              <div className="service-category-header">
                <div className="service-category-icon"><i className="fas fa-cogs"></i></div>
                <div>
                  <h3>Content & Ops (Behind the Scenes)</h3>
                  <p>AI that runs your website more efficiently</p>
                </div>
              </div>
              <div className="service-category-features">
                {[
                  { icon: 'fa-pen-fancy', title: 'Auto-Generated Blog/SEO Content', desc: 'From existing product data and pages' },
                  { icon: 'fa-tags', title: 'AI Meta-Description Generator', desc: 'Optimized SEO tags for every page' },
                  { icon: 'fa-file-alt', title: 'Auto-Summarize Pages/PDFs', desc: 'Convert long content into FAQs' },
                  { icon: 'fa-universal-access', title: 'AI Alt-Text & Accessibility', desc: 'Auto-generated image alt-text and compliance' },
                ].map((feature, i) => (
                  <div className="feature-list-item" key={i}>
                    <div className="feature-list-icon"><i className={`fas ${feature.icon}`}></i></div>
                    <div>
                      <strong>{feature.title}</strong>
                      <p>{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="services-cta">
            <Link to="/services" className="btn btn-primary btn-lg">View All Services <i className="fas fa-arrow-right"></i></Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="how-it-works">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">How It Works</span>
             <h2 className="section-title">4 Simple Steps to <span className="gradient-text">AI powered growth</span></h2>
             <p className="section-description">We make adding AI to your website simple, transparent, and results driven.</p>
          </div>
          <div className="steps-grid">
            {[
              { icon: 'fa-search', title: 'Audit', desc: 'We analyze your current website, identify opportunities, and recommend the best AI features for your business.' },
              { icon: 'fa-clipboard-list', title: 'Recommend', desc: 'We present a custom AI stack tailored to your goals, timeline, and budget with no jargon, just clear options.' },
              { icon: 'fa-code', title: 'Install', desc: 'Our team installs the AI features on your existing website. No rebuild needed. Most features go live in 7-14 days.' },
              { icon: 'fa-chart-line', title: 'Maintain & Optimize', desc: 'We monitor, update, and improve your AI features daily, weekly, monthly to ensure peak performance and ROI.' },
            ].map((step, i) => (
              <div className="step-item" key={i}>
                <div className="step-number">{i + 1}</div>
                <div className="step-icon"><i className={`fas ${step.icon}`}></i></div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section id="pricing-preview" className="pricing-preview">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Pricing</span>
            <h2 className="section-title">Simple, <span className="gradient-text">Transparent Pricing</span></h2>
            <p className="section-description">
              Fixed-price packages with no hidden fees. One-time setup + monthly maintenance.
            </p>
          </div>
          <div className="pricing-grid">
             {pricing.plans.map((plan, i) => {
               const base = DEFAULT_HOME_PLANS[i] || DEFAULT_HOME_PLANS[0]
               return (
               <div className={`pricing-card ${base.featured ? 'featured' : ''}`} key={i}>
                 {base.badge && <div className="pricing-badge">{base.badge}</div>}
                 <h3>{base.name}</h3>
                 <div className="pricing-amount">{pricing.symbol}{plan.price}</div>
                 <div className="pricing-period">{base.period} setup + {pricing.symbol}{plan.maintenance}/mo maintenance</div>
                 <ul>
                   {base.features.map((f, j) => (
                     <li key={j}><i className="fas fa-check"></i> {f}</li>
                   ))}
                 </ul>
                 <Link to="/contact" className={`btn ${base.featured ? 'btn-primary' : 'btn-outline'} btn-block`}>
                   Get Started <i className="fas fa-arrow-right"></i>
                 </Link>
               </div>
             )})}
          </div>
          <div className="pricing-cta">
            <Link to="/pricing" className="btn btn-outline btn-lg">View Full Pricing Details <i className="fas fa-list"></i></Link>
          </div>
        </div>
      </section>

      {/* Testimonials
      <section id="testimonials" className="testimonials">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Testimonials</span>
            <h2 className="section-title">Trusted by <span className="gradient-text">Local Business Owners</span></h2>
          </div>
          <div className="testimonials-grid">
            {[
              { name: 'Rajesh Kumar', business: 'Owner, Kumar Electronics', text: 'The AI chatbot Tredex installed now handles 70% of our support queries. Our customers love getting instant answers after hours.', rating: 5 },
              { name: 'Priya Sharma', business: 'Founder, Glow Beauty Salon', text: 'We used to lose so many leads from our website. Now the AI lead qualifier captures every visitor and sends us qualified appointments.', rating: 5 },
              { name: 'Arun Patel', business: 'Director, Patel Real Estate', text: 'Smart search and AI recommendations transformed our property portal. Visitors find what they need faster, and inquiries are up 45%.', rating: 5 },
              { name: 'Sneha Reddy', business: 'Owner, FreshBite Restaurant', text: 'The multilingual translation feature helped us serve Telugu and Hindi-speaking customers. Order inquiries doubled in a month.', rating: 5 },
            ].map((testimonial, i) => (
              <div className="testimonial-card" key={i}>
                <div className="testimonial-stars">
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <i key={j} className="fas fa-star"></i>
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar"><i className="fas fa-user"></i></div>
                  <div>
                    <strong>{testimonial.name}</strong>
                    <span className="testimonial-business">{testimonial.business}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Stats Section */}
      <section className="stats-section">
  <div className="container">
    <div className="stats-grid" ref={statsRef}>
      {[
        {
          icon: 'fa-building',
          value: '200%',
          label: 'Potential Business Transformation'
        },
        {
          icon: 'fa-percentage',
          value: '40%',
          label: 'Average Increase in Leads'
        },
        {
          icon: 'fa-ticket-alt',
          value: '60%',
          label: 'Fewer Support Tickets'
        },
        {
          icon: 'fa-clock',
          value: '24/7',
          
          label: 'AI Availability'
        }
      ].map((stat, i) => {
        const isStatic = stat.value === '24/7'

        return (
          <div className="stat-card" key={i}>
            <div className="stat-icon">
              <i className={`fas ${stat.icon}`}></i>
            </div>

            <div className="stat-content">
              <span
                className="stat-number"
                {...(!isStatic && {
                  'data-target': stat.value.replace(/\D/g, '')
                })}
              >
                {isStatic ? '24/7' : '0'}
              </span>

              {!isStatic && (
                <span className="stat-suffix">
                  {stat.value.includes('+')
                    ? '+'
                    : stat.value.includes('%')
                      ? '%'
                      : ''}
                </span>
              )}

              <span className="stat-label">
                {stat.label}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  </div>
</section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-wrapper">
            <div className="cta-content">
              <span className="section-badge">Get Started</span>
              <h2 className="cta-title">Ready to Add <span className="gradient-text">AI</span> to Your Business?</h2>
              <p className="cta-description">Get a free AI audit of your current website. We'll identify quick wins and recommend the perfect AI features for your business.</p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn btn-primary btn-lg">Contact Now <i className="fas fa-paper-plane"></i></Link>
                <Link to="/services" className="btn btn-outline btn-lg">Explore Services <i className="fas fa-th-large"></i></Link>
              </div>
             
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Contact Us</span>
            <h2 className="section-title">Let's Discuss Your <span className="gradient-text">AI Needs</span></h2>
            <p className="section-description">Tell us about your business and website. We'll send you a free AI audit within 24 hours.</p>
          </div>
         <div className="contact-wrapper">
  <div className="contact-info">

    <div className="contact-info-card">
      <div className="contact-info-icon">
        <i className="fas fa-envelope"></i>
      </div>
      <h4>Email Us</h4>
      <a
        href={`mailto:${supportEmail.mailto}`}
        dangerouslySetInnerHTML={{ __html: supportEmail.label }}
      />
    </div>

    <div className="contact-info-card">
      <div className="contact-info-icon">
        <i className="fas fa-phone-alt"></i>
      </div>
      <h4>Call Us</h4>
      <a href="tel:+919677255272">
        +91 96772 55272
      </a>
    </div>

    <div className="contact-info-card">
      <div className="contact-info-icon">
        <i className="fas fa-map-marker-alt"></i>
      </div>
      <h4>Our HQ</h4>
      <p>Chennai, Tamil Nadu, India</p>
    </div>

  </div>

  <div className="contact-form-wrapper">
    <ContactForm />
  </div>
</div>
        </div>
      </section>

      <Footer />
    </>
  )
}
